import { createFilterOptions } from '@mui/material';
import { getCountryDataList } from 'countries-list';
import { ComponentProps, useMemo } from 'react';

import Autocomplete from 'src/components/Autocomplete';
import { ChevronDownIcon } from 'src/components/icons/ChevronDown';
import { BaseTextField } from 'src/legacy/components/TextField';

type CountryData = ReturnType<typeof getCountryDataList>[number];

const PINNED_COUNTRY_ISO2S = ['US', 'CA', 'AU', 'GB'];

const filterCountries = createFilterOptions<CountryData>({
  stringify: (option) => option.name,
});

interface CountryInputProps
  extends Pick<
    ComponentProps<typeof BaseTextField>,
    'label' | 'error' | 'helperText'
  > {
  className?: string;
  country: string;
  onCountryChange: (value: string | null) => void;
  autoFocus?: boolean;
}

const CountryInput = ({
  className,
  country,
  onCountryChange,
  label,
  error,
  helperText,
  autoFocus,
}: CountryInputProps) => {
  const baseCountries = useMemo(
    () => getCountryDataList().sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  const sortedCountries = useMemo(() => {
    const pinnedSet = new Set(PINNED_COUNTRY_ISO2S);
    const pinned = PINNED_COUNTRY_ISO2S.map((iso2) =>
      baseCountries.find((c) => c.iso2 === iso2),
    ).filter((c): c is CountryData => c !== undefined);
    const rest = baseCountries.filter((c) => !pinnedSet.has(c.iso2));
    return [...pinned, ...rest];
  }, [baseCountries]);

  const selectCountryInfo = useMemo(() => {
    if (country === '') {
      return null;
    }

    return baseCountries.find((c) => c.iso2 === country);
  }, [country, baseCountries]);

  return (
    <Autocomplete
      className={className}
      classes={{
        option: 'min-h-0',
      }}
      sx={{
        // Counter MUI v5 Autocomplete defaults that inflate the input past
        // BaseTextField's 30px skinny height (see MuiAutocomplete.ts comment).
        '& .MuiOutlinedInput-root': { padding: '0px 7px' },
        '&.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root':
          { paddingRight: '14px' },
      }}
      value={selectCountryInfo}
      options={sortedCountries}
      filterOptions={filterCountries}
      getOptionLabel={(option) => option.name}
      onChange={(_, value) => {
        onCountryChange(value ? value.iso2 : null);
      }}
      renderInput={(params) => (
        <BaseTextField
          {...params}
          label={label}
          variant="outlined"
          sizeVariant="skinny"
          fullWidth
          error={error}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            className: '',
            autoFocus,
            endAdornment: (
              <ChevronDownIcon className="w-[16px] h-[16px] text-secondary" />
            ),
          }}
          inputProps={{ ...params.inputProps, 'data-1p-ignore': true }}
        />
      )}
    />
  );
};

export default CountryInput;
